import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { accessRequestAPI, authAPI } from '../services/api';
import { Bus as BusIcon, LogOut, Mail, User, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';

interface AccessRequestProps {
  userInfo: {
    email: string;
    name: string;
    picture: string;
  };
}

export const AccessRequest: React.FC<AccessRequestProps> = ({ userInfo }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestStatus, setRequestStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRequestStatus();
  }, []);

  const fetchRequestStatus = async () => {
    try {
      setIsLoading(true);
      const response = await accessRequestAPI.getRequestStatus(userInfo.email);
      if (response.status === 'success') {
        setRequestStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching request status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please provide a reason for your access request');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await accessRequestAPI.createRequest(
        userInfo.email,
        userInfo.name,
        message.trim()
      );

      if (response.status === 'success') {
        toast.success(response.message);
        setMessage('');
        fetchRequestStatus(); // Refresh status
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Error submitting request:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to submit access request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      // Clear all authentication and user data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('unauthorized_user_info');
      window.location.reload();
    } catch (error) {
      // Even if API fails, still log out locally
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('unauthorized_user_info');
      window.location.reload();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'approved':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'rejected':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-red-600 p-3 rounded-2xl">
              <BusIcon className="h-12 w-12 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-red-600 p-3 rounded-2xl">
              <BusIcon className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Access Request Required
          </h2>
          <p className="text-gray-600">
            Your account needs admin approval to access the TNSTC Bus Monitor
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* User Info Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              {userInfo.picture ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="h-12 w-12 rounded-full"
                />
              ) : (
                <div className="h-12 w-12 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-600" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{userInfo.name}</h3>
                <p className="text-sm text-gray-600 flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  {userInfo.email}
                </p>
              </div>
              <div className="ml-auto">
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Current Request Status */}
            {requestStatus && requestStatus.has_request ? (
              <div className={`rounded-lg border p-4 mb-6 ${getStatusColor(requestStatus.status)}`}>
                <div className="flex items-center space-x-3">
                  {getStatusIcon(requestStatus.status)}
                  <div className="flex-1">
                    <h4 className="font-semibold capitalize">
                      Request {requestStatus.status}
                    </h4>
                    <p className="text-sm mt-1">
                      Submitted on {formatDate(requestStatus.requested_at)}
                    </p>
                    {requestStatus.processed_at && (
                      <p className="text-sm">
                        Processed on {formatDate(requestStatus.processed_at)}
                      </p>
                    )}
                    {requestStatus.admin_notes && (
                      <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-sm">
                        <strong>Admin Notes:</strong> {requestStatus.admin_notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Request Form */}
            {!requestStatus?.has_request || requestStatus.status === 'rejected' ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Request Access
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Please explain why you need access to the TNSTC Bus Monitor application. 
                    An administrator will review your request and respond accordingly.
                  </p>
                </div>

                <form onSubmit={handleSubmitRequest} className="space-y-4">
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Access Request *
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Please explain why you need access to this application..."
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !message.trim()}
                    className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting Request...
                      </>
                    ) : (
                      'Submit Access Request'
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mb-4">
                  {requestStatus.status === 'pending' ? (
                    <Clock className="h-16 w-16 text-yellow-500 mx-auto" />
                  ) : requestStatus.status === 'approved' ? (
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                  ) : (
                    <XCircle className="h-16 w-16 text-red-500 mx-auto" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {requestStatus.status === 'pending' && 'Request Under Review'}
                  {requestStatus.status === 'approved' && 'Access Approved!'}
                  {requestStatus.status === 'rejected' && 'Request Rejected'}
                </h3>
                <p className="text-gray-600">
                  {requestStatus.status === 'pending' && 'Your access request is being reviewed by an administrator. You will be notified once a decision is made.'}
                  {requestStatus.status === 'approved' && 'Your access has been approved! Please log out and log back in to access the application.'}
                  {requestStatus.status === 'rejected' && 'Your access request was rejected. You can submit a new request if needed.'}
                </p>
                {requestStatus.status === 'approved' && (
                  <button
                    onClick={handleLogout}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout & Login Again
                  </button>
                )}
              </div>
            )}

            {/* Help Information */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <BusIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Need Help?</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    If you believe you should have access to this application, please contact your administrator 
                    or IT support team. Make sure to include your email address ({userInfo.email}) in your request.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 